import type {
  ETLSourceResponse,
  ListAllETLSourcesResponse,
  SourceStatusEvent,
} from '@/model/etlSource/dto.generated';

export function providerProjectLabel(providerProjectName: string): string {
  const lastDash = providerProjectName.lastIndexOf('-');
  return lastDash > 0 ? providerProjectName.slice(0, lastDash) : providerProjectName;
}

export type EtlSourceListDraft = { data?: Partial<ListAllETLSourcesResponse> };

export function getSourceItemsFromDraft(
  draft: EtlSourceListDraft | undefined,
): ETLSourceResponse[] | undefined {
  return draft?.data?.items;
}

function sanitizeAthenaName(value: string): string {
  const stripped = value
    .trim()
    .normalize('NFD')
    .replace(/\p{Mn}+/gu, '');
  return stripped.replace(/[^a-zA-Z0-9_]+/g, '_').toLowerCase();
}

export type PreviewTableOption = { label: string; value: string };

export function toPreviewTableOptions(
  sourceName: string,
  sourceType: string,
  tables: readonly { name: string }[],
): PreviewTableOption[] {
  return tables.map((table) => ({
    label: table.name,
    value: canonicalTableName(sourceName, sourceType, table.name),
  }));
}

function s3FileStem(path: string): string {
  const segments = path.split('/');
  let file = segments.length - 1;
  while (file >= 0 && (segments[file] ?? '').lastIndexOf('.') <= 0) {
    file--;
  }
  if (file < 0) return segments[segments.length - 1] ?? path;
  const fileSegment = segments[file] ?? '';
  const base = fileSegment.slice(0, fileSegment.lastIndexOf('.'));
  return [base, ...segments.slice(file + 1)].join('_');
}

export function canonicalTableName(
  sourceName: string,
  sourceType: string,
  bareName: string,
): string {
  const stem = sourceType.toLowerCase() === 's3' ? s3FileStem(bareName) : bareName;
  return sanitizeAthenaName(`${sourceName}_${stem}`);
}

export type UiState = {
  isTerminal: boolean;
  isError: boolean;
  shouldPoll: boolean;
  label: string;
  tone: 'progress' | 'success' | 'error' | 'muted';
};

export interface EtlLiveEvent {
  projectId: string;
  sourceId: string;
  reason: string;
}

function isSourceStatusEvent(value: unknown): value is SourceStatusEvent {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;

  return typeof record['project_id'] === 'string' && typeof record['reason'] === 'string';
}

export function toEtlLiveEvent(frame: string): EtlLiveEvent | null {
  const parsed: unknown = JSON.parse(frame);
  if (!isSourceStatusEvent(parsed)) return null;

  return { projectId: parsed.project_id, sourceId: parsed.source_id ?? '', reason: parsed.reason };
}

const DEFAULT_RETRY_AFTER_MS = 30_000;
const MAX_RETRY_AFTER_MS = 300_000;

export function parseRetryAfterMs(header: string | null): number {
  const seconds = Number(header);
  if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_RETRY_AFTER_MS;

  return Math.min(seconds * 1_000, MAX_RETRY_AFTER_MS);
}
